/**
 * Actions « en ligne » compatibles CSP stricte (sans `'unsafe-inline'` pour les scripts).
 *
 * Les anciens gestionnaires HTML `onclick="…"` exécutent du code arbitraire dans l'attribut : une CSP qui
 * interdit les scripts en ligne les bloque tous, et une CSP qui les autorise laisse aussi passer un `onerror=…`
 * injecté dans une donnée mal échappée. Ils sont remplacés par des attributs `data-on-click="fonction('a', 1)"`
 * traités ICI, par un seul écouteur délégué :
 *  - grammaire minuscule et stricte : une suite d'appels `nom(arguments)` séparés par `;` ; arguments = chaîne,
 *    nombre, booléen, `null`, tableau de nombres, `this`, `event`, `this.value`, `this.checked` ;
 *  - AUCUN `eval`, `Function`, ni exécution de texte : le code de l'attribut n'est jamais évalué ;
 *  - seules les fonctions de la liste blanche `INLINE_ACTION_NAMES` (ou les actions intégrées `nx_*`) sont
 *    appelables : un `data-on-click` injecté vers une fonction du navigateur (réseau, évaluation de texte…) est ignoré.
 */
import { INLINE_ACTION_NAMES } from '@presentation/shell/inlineActionNames';

export type InlineEventName = 'click' | 'change' | 'input' | 'mouseleave';
export const INLINE_EVENTS: readonly InlineEventName[] = ['click', 'change', 'input', 'mouseleave'];

export type InlineArg =
  | string
  | number
  | boolean
  | null
  | number[]
  | { ref: 'this' | 'event' | 'this.value' | 'this.checked' };

export interface InlineCall {
  name: string;
  args: InlineArg[];
}

const NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
const NUMBER_RE = /^-?\d+(?:\.\d+)?$/;

/** Découpe sur un séparateur hors guillemets / crochets ; null si guillemets ou crochets mal appariés. */
function splitTopLevel(text: string, separator: string): string[] | null {
  const parts: string[] = [];
  let current = '';
  let quote: string | null = null;
  let depth = 0;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text.charAt(i);
    if (quote) {
      current += ch;
      if (ch === '\\') {
        i += 1;
        if (i >= text.length) return null;
        current += text.charAt(i);
      } else if (ch === quote) {
        quote = null;
      }
      continue;
    }
    if (ch === "'" || ch === '"') {
      quote = ch;
      current += ch;
    } else if (ch === '[') {
      depth += 1;
      current += ch;
    } else if (ch === ']') {
      depth -= 1;
      if (depth < 0) return null;
      current += ch;
    } else if (ch === separator && depth === 0) {
      parts.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  if (quote || depth !== 0) return null;
  parts.push(current);
  return parts;
}

function parseString(token: string): string | null {
  const quote = token.charAt(0);
  if (
    (quote !== "'" && quote !== '"') ||
    token.length < 2 ||
    token.charAt(token.length - 1) !== quote
  )
    return null;
  let out = '';
  for (let i = 1; i < token.length - 1; i += 1) {
    const ch = token.charAt(i);
    if (ch === quote) return null; // guillemet non échappé au milieu : jeton invalide
    if (ch === '\\') {
      i += 1;
      const next = token.charAt(i);
      if (next !== quote && next !== '\\') return null;
      out += next;
    } else {
      out += ch;
    }
  }
  return out;
}

export function parseInlineArg(raw: string): InlineArg | undefined {
  const token = raw.trim();
  if (token === 'true') return true;
  if (token === 'false') return false;
  if (token === 'null') return null;
  if (token === 'this' || token === 'event' || token === 'this.value' || token === 'this.checked') {
    return { ref: token };
  }
  if (NUMBER_RE.test(token)) return Number(token);
  if (token.startsWith('[') && token.endsWith(']')) {
    const inner = token.slice(1, -1).trim();
    if (inner === '') return [];
    const items = inner.split(',').map((item) => item.trim());
    if (!items.every((item) => NUMBER_RE.test(item))) return undefined;
    return items.map(Number);
  }
  const str = parseString(token);
  return str === null ? undefined : str;
}

/** Analyse stricte d'un attribut : null (et rien ne s'exécute) à la moindre syntaxe hors grammaire. */
export function parseInlineAction(code: string): InlineCall[] | null {
  const statements = splitTopLevel(code, ';');
  if (!statements) return null;
  const calls: InlineCall[] = [];
  for (const statement of statements) {
    const text = statement.trim();
    if (text === '') continue;
    const open = text.indexOf('(');
    if (open <= 0 || !text.endsWith(')')) return null;
    const name = text.slice(0, open).trim();
    if (!NAME_RE.test(name)) return null;
    const inner = text.slice(open + 1, -1).trim();
    const args: InlineArg[] = [];
    if (inner !== '') {
      const tokens = splitTopLevel(inner, ',');
      if (!tokens) return null;
      for (const token of tokens) {
        const arg = parseInlineArg(token);
        if (arg === undefined) return null;
        args.push(arg);
      }
    }
    calls.push({ name, args });
  }
  return calls.length > 0 ? calls : null;
}

/** Actions intégrées (remplacent les anciens `location.reload()`, `window.print()`, etc.). */
const BUILTINS: Record<string, (...args: unknown[]) => void> = {
  nx_reload: () => {
    window.location.reload();
  },
  nx_print: () => {
    window.print();
  },
  nx_preventDefault: (event) => {
    if (event instanceof Event) event.preventDefault();
  },
  nx_clickById: (id) => {
    if (typeof id === 'string') document.getElementById(id)?.click();
  },
  nx_removeClass: (element, className) => {
    if (element instanceof Element && typeof className === 'string')
      element.classList.remove(className);
  },
};

const ALLOWED = new Set<string>(INLINE_ACTION_NAMES);

export interface InlineActionHost {
  /** Résout une fonction globale appelable ; undefined si absente (ou non autorisée). */
  resolve(name: string): ((...args: unknown[]) => unknown) | undefined;
}

export const windowHost: InlineActionHost = {
  resolve(name) {
    if (!ALLOWED.has(name)) return undefined;
    const candidate = (window as unknown as Record<string, unknown>)[name];
    return typeof candidate === 'function'
      ? (candidate as (...args: unknown[]) => unknown)
      : undefined;
  },
};

function resolveArg(arg: InlineArg, element: Element, event: Event): unknown {
  if (typeof arg === 'object' && arg !== null && !Array.isArray(arg)) {
    switch (arg.ref) {
      case 'this':
        return element;
      case 'event':
        return event;
      case 'this.value':
        return (element as HTMLInputElement).value;
      case 'this.checked':
        return (element as HTMLInputElement).checked;
    }
  }
  return arg;
}

/** Exécute un attribut déjà analysé ; renvoie false si le code est refusé. */
export function runInlineAction(
  code: string,
  element: Element,
  event: Event,
  host: InlineActionHost = windowHost,
): boolean {
  const calls = parseInlineAction(code);
  if (!calls) {
    console.warn('inline_action_rejected');
    return false;
  }
  for (const call of calls) {
    const target = Object.hasOwn(BUILTINS, call.name)
      ? BUILTINS[call.name]
      : host.resolve(call.name);
    if (!target) {
      console.warn(`inline_action_unknown:${call.name}`);
      return false;
    }
    const result: unknown = target(...call.args.map((arg) => resolveArg(arg, element, event)));
    if (result instanceof Promise) {
      result.catch((error: unknown) => {
        console.error(`inline_action_failed:${call.name}`, error);
      });
    }
  }
  return true;
}

function elementOf(target: EventTarget | null): Element | null {
  if (target instanceof Element) return target;
  if (target instanceof Node) return target.parentElement;
  return null;
}

/**
 * Installe les écouteurs délégués (une fois). `mouseleave` ne remonte pas : il est capté en phase de capture et
 * ne concerne que l'élément quitté, comme l'attribut `onmouseleave` d'origine.
 */
export function installInlineActions(
  doc: Document = document,
  host: InlineActionHost = windowHost,
): () => void {
  const disposers: Array<() => void> = [];
  for (const type of INLINE_EVENTS) {
    const attribute = `data-on-${type}`;
    const listener = (event: Event): void => {
      let node = elementOf(event.target);
      while (node) {
        const code = node.getAttribute(attribute);
        if (code) runInlineAction(code, node, event, host);
        if (type === 'mouseleave' || event.cancelBubble) break;
        node = node.parentElement;
      }
    };
    const capture = type === 'mouseleave';
    doc.addEventListener(type, listener, capture);
    disposers.push(() => {
      doc.removeEventListener(type, listener, capture);
    });
  }
  return () => {
    for (const dispose of disposers) dispose();
  };
}
